import React, {memo, useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';

import {getFilePublicLink} from 'mattermost-redux/actions/files';
import {getFilePublicLink as selectFilePublicLink} from 'mattermost-redux/selectors/entities/files';
import {type FileInfo} from 'mattermost-redux/types/files';

import OverlayTrigger from 'components/overlay_trigger';
import Tooltip from 'components/tooltip';

import Constants from 'utils/constants';
import {copyToClipboard} from 'utils/copy_to_clipboard';

import {isFileInfo, type LinkInfo} from '../types';

import './file_preview_modal_main_actions.scss';

interface DownloadLinkProps {
    download?: string;
}

interface Props {
    usedInside?: 'Header' | 'Footer';
    showOnlyClose?: boolean;
    showClose?: boolean;
    showPublicLink?: boolean;
    filename: string;
    fileURL: string;
    fileInfo: FileInfo | LinkInfo;
    enablePublicLink: boolean;
    canDownloadFiles: boolean;
    handleModalClose: () => void;
    onDownload?: () => void;
}

const FilePreviewModalMainActions = (props: Props) => {
    const {
        showOnlyClose = false,
        usedInside = 'Header',
        showClose = true,
        showPublicLink = true,
        fileInfo,
        enablePublicLink,
        handleModalClose,
        onDownload,
        filename,
        canDownloadFiles,
        fileURL,
    } = props;

    const tooltipPlacement = usedInside === 'Header' ? 'bottom' : 'top';
    const selectedFilePublicLink = useAppSelector((state) => selectFilePublicLink(state)?.link);
    const dispatch = useAppDispatch();
    const [publicLinkCopied, setPublicLinkCopied] = useState(false);

    useEffect(() => {
        if (isFileInfo(fileInfo) && enablePublicLink) {
            dispatch(getFilePublicLink(fileInfo.id));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileInfo, enablePublicLink]);

    const copyPublicLink = () => {
        copyToClipboard(selectedFilePublicLink ?? '');
        setPublicLinkCopied(true);
    };

    const closeButton = (
        <OverlayTrigger
            delayShow={Constants.OVERLAY_TIME_DELAY}
            key='publicLink'
            placement={tooltipPlacement}
            overlay={
                <Tooltip id='close-icon-tooltip'>
                    <FormattedMessage
                        id='full_screen_modal.close'
                        defaultMessage='Close'
                    />
                </Tooltip>
            }
        >
            <button
                aria-label='Close'
                className='file-preview-modal-main-actions__action-item'
                onClick={handleModalClose}
            >
                <i className='icon icon-close'/>
            </button>
        </OverlayTrigger>
    );

    let publicTooltipMessage = (
        <FormattedMessage
            id='view_image_popover.publicLink'
            defaultMessage='Get a public link'
        />
    );

    if (publicLinkCopied) {
        publicTooltipMessage = (
            <FormattedMessage
                id='file_preview_modal_main_actions.public_link-copied'
                defaultMessage='Public link copied'
            />
        );
    }

    const publicLink = (
        <OverlayTrigger
            delayShow={Constants.OVERLAY_TIME_DELAY}
            key='filePreviewPublicLink'
            placement={tooltipPlacement}
            shouldUpdatePosition={true}
            onExit={() => setPublicLinkCopied(false)}
            overlay={
                <Tooltip id='link-variant-icon-tooltip'>
                    {publicTooltipMessage}
                </Tooltip>
            }
        >
            <a
                href='#'
                className='file-preview-modal-main-actions__action-item'
                onClick={copyPublicLink}
            >
                <i className='icon icon-link-variant'/>
            </a>
        </OverlayTrigger>
    );

    const downloadLinkProps: DownloadLinkProps = {};
    downloadLinkProps.download = filename;

    const canCopyPublicLink = enablePublicLink && showPublicLink && Boolean(selectedFilePublicLink);

    const download = (
        <OverlayTrigger
            delayShow={Constants.OVERLAY_TIME_DELAY}
            key='download'
            placement={tooltipPlacement}
            overlay={
                <Tooltip id='download-icon-tooltip'>
                    <FormattedMessage
                        id='view_image_popover.download'
                        defaultMessage='Download'
                    />
                </Tooltip>
            }
        >
            <a
                href={fileURL}
                className='file-preview-modal-main-actions__action-item'
                target='_blank'
                rel='noopener noreferrer'
                download={filename}
                onClick={onDownload}
            >
                <i className='icon icon-download-outline'/>
            </a>
        </OverlayTrigger>
    );

    return (
        <div className='file-preview-modal-main-actions__actions'>
            {!showOnlyClose && canCopyPublicLink && publicLink}
            {!showOnlyClose && canDownloadFiles && download}
            {showClose && closeButton}
        </div>
    );
};

export default memo(FilePreviewModalMainActions);
